import { Typography, TypographyProps } from '@mui/material'
import { useMemo } from 'react'
import { ICurrencyFormatOption } from 'Services/localize/currencyFormat'
import { currency } from '../utils/currency'
import { useCustomerFacingDisplayContext } from 'pages/CustomerFacingDisplayPage/context/CustomerFacingDisplayProvider'

type ForceMonetaryFormat = 'minus' | 'plus'

interface MonetaryFormatProps
    extends TypographyProps,
        Omit<ICurrencyFormatOption, 'fraction'> {
    value: number | undefined | null
    /**
     * Override Value Monetary Format
     */
    forceMonetaryFormat?: ForceMonetaryFormat
    readonly?: boolean
    isOnlyString?: boolean
    currencyCode?: string
    isAccounting?: boolean
    component?: 'span'
    language?: string
}

/** Use in CFD only */
export default function MonetaryFormat(props: MonetaryFormatProps) {
    const {
        value,
        forceMonetaryFormat,
        readonly = false,
        isOnlyString = false,
        currencyCode: propsCurrencyCode,
        isAccounting = false,
        signDisplay = false,
        language: propsLanguage,
        ...other
    } = props

    const {
        operationalSetting: {
            currencyCode: contextCurrencyCode,
            language: contextLanguage,
        },
    } = useCustomerFacingDisplayContext()

    // Use props values if provided, otherwise fall back to context values with defaults
    const finalCurrencyCode = propsCurrencyCode || contextCurrencyCode || 'USD'
    const finalLanguage = propsLanguage || contextLanguage || 'en'

    const normalizeValue = value || 0
    const formatText = useMemo(() => {
        return monetaryFormatText(
            {
                value,
                forceMonetaryFormat,
                currencyCode: finalCurrencyCode,
                isAccounting,
                signDisplay,
                language: finalLanguage,
            },
            currency,
        )
    }, [
        finalCurrencyCode,
        forceMonetaryFormat,
        isAccounting,
        signDisplay,
        value,
        finalLanguage,
    ])

    if (isOnlyString) return <>{formatText}</>

    return (
        <span>
            <Typography
                {...other}
                color={
                    other.color
                        ? other.color
                        : normalizeValue === 0
                        ? 'text'
                        : (normalizeValue < 0 &&
                              forceMonetaryFormat !== 'plus') ||
                          forceMonetaryFormat === 'minus'
                        ? 'error'
                        : 'primary'
                }
                style={readonly ? { color: '#00000042' } : {}}
            >
                {formatText}
            </Typography>
        </span>
    )
}

interface CurrencyFunction {
    (
        value: number,
        language: string,
        currencyCode: string,
        options?: ICurrencyFormatOption,
    ): string
}

export const monetaryFormatText = (
    {
        value,
        forceMonetaryFormat,
        currencyCode = 'USD',
        isAccounting,
        language = 'en',
    }: MonetaryFormatProps,
    currency: CurrencyFunction,
) => {
    const normalizeValue = value || 0
    const absValue = Math.abs(normalizeValue)
    const sign =
        normalizeValue === 0
            ? 1
            : forceMonetaryFormat === 'plus'
            ? 1
            : forceMonetaryFormat === 'minus'
            ? -1
            : normalizeValue < 0
            ? -1
            : 1

    return currency(absValue * sign, language, currencyCode, {
        isAccounting: isAccounting,
        fraction: true,
        signDisplay: false,
    })
}
